import { ListItemProps } from '@sato/components';

import {
  ApartmentFeature,
  Dishwasher,
  FloorMaterial,
  FridgeType,
  Stove,
  WashingMachine,
} from '@models/apartment.types';
import { LanguageKey } from '@models/Language';

export type ApartmentDetailProps = Pick<
  ListItemProps,
  'children' | 'label' | 'tooltip'
>;

export const featureTranslationMap = {
  APARTMENT_SAUNA: {
    label: 'saunaLabel',
    children: 'yes',
    tooltip: 'saunaTooltip',
  },
  ELEVATOR: {
    label: 'elevatorLabel',
    children: 'yes',
  },
  BALCONY: {
    label: 'balconyLabel',
    children: 'yes',
  },
} satisfies Partial<Record<ApartmentFeature, ApartmentDetailProps>>;

export const fridgeTranslationMap = {
  FRIDGE: 'basicFridgeType',
  FREEZER: 'freezerFridgeType',
  COMBINATION: 'combinationFridgeType',
  INTEGRATED: 'integratedFridgeType',
  LOW: 'lowFridgeType',
  YANKEE: 'yankeeFridgeType',
  FRIDGE_AND_FREEZER: 'fridgeFreezerFridgeType',
  COMBINATION_AND_COMBINATION: 'combinationAndCombinationFridgeType',
} satisfies Record<FridgeType, string>;

export const washingMachineTranslationMap = {
  [WashingMachine.INPUT_CONNECTION]: 'washing_machine.input_connection',
  [WashingMachine.INPUT_OUTPUT_CONNECTIONS]:
    'washing_machine.input_output_connection',
  [WashingMachine.OUTPUT_CONNECTION]: 'washing_machine.output_connection',
  [WashingMachine.WASHINGMACHINE_INCLUDED]: 'washing_machine.included',
  [WashingMachine.NOT_INSTALLABLE]: 'washing_machine.not_installable',
} satisfies Record<WashingMachine, string>;

export const stoveTranslationMap = {
  [Stove.CERAMIC]: 'stove_type.ceramic',
  [Stove.GAS]: 'stove_type.gas',
  [Stove.INDUCTION]: 'stove_type.induction',
  [Stove.TRADITIONAL]: 'stove_type.traditional',
  [Stove.TWO_PLATE]: 'stove_type.two_plate',
} satisfies Record<Stove, string>;

export const dishwasherTranslationMap = {
  [Dishwasher.CONNECTIONS]: 'connectionsDishwasherType',
  [Dishwasher.INTEGRATED]: 'integratedDishwasherType',
  [Dishwasher.YES]: 'yes',
  [Dishwasher.NO]: 'no',
} satisfies Record<Dishwasher, string>;

export const heatingSystemTranslationMap = {
  GEOTHERMAL: 'geothermalHeatingSystemType',
  // TODO add typing for heating systems form API
} satisfies Record<string, string>;

export const floorMaterialTranslationMap = {
  PLANK: 'floor_type_plank',
  LAMINATE: 'floor_type_laminate',
  CORK: 'floor_type_cork',
  PLASTIC: 'floor_type_plastic',
  TILE: 'floor_type_tile',
  MASS: 'floor_type_mass',
  VINYL: 'floor_type_vinyl',
  PARQUET: 'floor_type_parquet',
} satisfies Record<FloorMaterial, string>;

export const apartmentRoomTypeTranslationsMap = {
  studio: {
    fi: 'solu',
    en: 'shared',
  },
  '1h': {
    fi: '1h',
    en: 'studio',
  },
  '2h': {
    fi: '2h',
    en: '1 bedroom',
  },
  '3h': {
    fi: '3h',
    en: '2 bedroom',
  },
  '4h': {
    fi: '4h',
    en: '3 bedroom',
  },
  '5h': {
    fi: '5h',
    en: '4 bedroom',
  },
  '6h': {
    fi: '6h',
    en: '5 bedroom',
  },
  '7h': {
    fi: '7h',
    en: '6 bedroom',
  },
  k: {
    fi: 'k',
    en: '',
  },
  kk: {
    fi: 'kk',
    en: '',
  },
  kt: {
    fi: 'kt',
    en: '',
  },
  alk: {
    fi: 'alkovi',
    en: 'alcove',
  },
  alkovi: {
    fi: 'alkovi',
    en: 'alcove',
  },
  s: {
    fi: 's',
    en: '',
  },
  ap: {
    fi: 'ap',
    en: '',
  },
  /*
   * This section is dynamic s.t. the room type translation code is able to compose the final room type
   * description based on whatever segment translations are available in the current language (as defined here).
   * In other words, different languages may have predefined translations for different segment combinations.
   * Hence this relaxed typing.
   */
} satisfies Record<string, Record<LanguageKey, string>>;

export type FormattedRoomTypeKey =
  keyof typeof apartmentRoomTypeTranslationsMap;

export const isFormattedRoomTypeKey = (
  key: string
): key is FormattedRoomTypeKey => key in apartmentRoomTypeTranslationsMap;
